// features/super-admin/pages/AuditLogsPage.tsx
/**
 * SUPER ADMIN — SYSTEM-WIDE AUDIT LOGS
 * Enterprise-grade audit trail with detail page navigation.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Search, Activity, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, X, Calendar,
    Building2, User, Shield, Key, FileText, ShoppingCart,
    DollarSign, Package, Truck, Users, CreditCard,
    Download, Eye, Clock, Layers,
    AlertTriangle, CheckCircle2, RefreshCw,
    ArrowUpDown, Filter, ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface AuditLog {
    id: string; tenantId: string; tenantName: string;
    userId: string; userName: string; userEmail: string;
    action: string; entityType: string; entityId: string | null;
    oldValues: Record<string, unknown> | null;
    newValues: Record<string, unknown> | null;
    ipAddress: string | null; userAgent: string | null;
    createdAt: string;
}

interface AuditLogsResponse {
    logs: AuditLog[]; totalCount: number;
    summary: { totalLogs: number; todayLogs: number; uniqueTenants: number; uniqueUsers: number };
}

// ============================================================
// ACTION GROUPS
// ============================================================

const ACTION_GROUPS: { label: string; actions: string[]; icon: React.ElementType; color: string; bg: string }[] = [
    { label: 'Authentication', actions: ['USER_LOGIN', 'USER_LOGOUT', 'PASSWORD_CHANGED', 'PIN_SET'], icon: Key, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Tenant', actions: ['TENANT_REGISTERED', 'TENANT_UPDATED', 'TENANT_DEACTIVATED', 'TENANT_ACTIVATED'], icon: Building2, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Staff', actions: ['STAFF_CREATED', 'STAFF_UPDATED', 'STAFF_DEACTIVATED', 'STAFF_REACTIVATED'], icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Business', actions: ['BUSINESS_CREATED', 'BUSINESS_UPDATED', 'BUSINESS_DEACTIVATED'], icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Products', actions: ['PRODUCT_CREATED', 'PRODUCT_UPDATED', 'PRODUCT_DELETED', 'PRODUCT_BULK_UPLOADED'], icon: Package, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Inventory', actions: ['INVENTORY_COUNT_STARTED', 'INVENTORY_COUNT_COMPLETED', 'INVENTORY_TRANSFER_CREATED', 'STOCK_ADJUSTED', 'STOCK_CONFIRMED'], icon: Layers, color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { label: 'Sales', actions: ['SALE_COMPLETED', 'SALE_VOIDED', 'SALE_REFUNDED'], icon: ShoppingCart, color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Finance', actions: ['BANK_ACCOUNT_CREATED', 'EXPENSE_CREATED', 'EXPENSE_APPROVED', 'INVOICE_CREATED'], icon: DollarSign, color: 'text-teal-600', bg: 'bg-teal-50' },
];

// ============================================================
// FORMATTERS
// ============================================================

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return 'N/A'; }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function AuditLogsPage() {
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);

    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        search: '', action: '', entityType: '',
        startDate: '', endDate: '',
        sortBy: 'createdAt', order: 'desc' as 'asc' | 'desc',
    });
    const [showFilters, setShowFilters] = useState(false);
    const limit = 20;

    const queryParams = useMemo(() => {
        const params: Record<string, any> = { limit, page };
        if (filters.search) params.search = filters.search;
        if (filters.action) params.action = filters.action;
        if (filters.entityType) params.entityType = filters.entityType;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        if (filters.sortBy) params.sortBy = filters.sortBy;
        if (filters.order) params.order = filters.order;
        return params;
    }, [filters, page]);

    const { data, isLoading, isError, refetch } = useQuery<AuditLogsResponse>({
        queryKey: ['super-admin-audit-logs', queryParams],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/audit-logs', { params: queryParams });
            return data?.data;
        },
        enabled: !!user,
    });

    const logs = data?.logs || [];
    const summary = data?.summary;
    const totalCount = data?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const hasActiveFilters = !!(filters.search || filters.action || filters.entityType || filters.startDate || filters.endDate);

    const getActionGroup = (action: string) => ACTION_GROUPS.find(g => g.actions.includes(action));

    const handleFilterChange = (key: keyof typeof filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ search: '', action: '', entityType: '', startDate: '', endDate: '', sortBy: 'createdAt', order: 'desc' });
        setPage(1);
    };

    const handleExportCSV = async () => {
        try {
            const { data } = await apiClient.get('/super-admin/audit-logs/export', { params: queryParams, responseType: 'blob' });
            const url = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { }
    };

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-slate-200 border-t-slate-600" />
        </div>
    );

    if (isError) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Failed to load audit logs" onRetry={() => refetch()} />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/super-admin')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 mb-3 group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Dashboard
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center shadow-lg">
                                <Activity size={20} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">System Audit Logs</h1>
                                <p className="text-slate-500 text-sm mt-0.5">{totalCount.toLocaleString()} events recorded across all tenants</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleExportCSV}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm">
                                <Download size={14} /> Export CSV
                            </button>
                            <button onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm">
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Summary Metrics */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Total Events', value: summary.totalLogs.toLocaleString(), icon: Activity, gradient: 'from-slate-500 to-slate-700' },
                            { label: 'Today', value: summary.todayLogs.toLocaleString(), icon: Calendar, gradient: 'from-blue-500 to-indigo-600' },
                            { label: 'Tenants', value: summary.uniqueTenants, icon: Building2, gradient: 'from-violet-500 to-purple-600' },
                            { label: 'Users', value: summary.uniqueUsers, icon: Users, gradient: 'from-emerald-500 to-teal-600' },
                        ].map((stat, i) => (
                            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                                <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                    <stat.icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input type="text" placeholder="Search by tenant, user, action, or entity..." value={filters.search}
                                onChange={e => handleFilterChange('search', e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-500/20" />
                            {filters.search && (
                                <button onClick={() => handleFilterChange('search', '')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400"><X size={14} /></button>
                            )}
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)}
                            className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all',
                                showFilters || hasActiveFilters ? 'bg-slate-100 text-slate-700 border-slate-300' : 'bg-slate-50 text-slate-600 border-slate-200')}>
                            <Filter size={14} /> Filters
                            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-slate-500" />}
                        </button>
                    </div>

                    <AnimatePresence>
                        {showFilters && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden border-t border-slate-100 pt-3">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Action Type</label>
                                        <select value={filters.action} onChange={e => handleFilterChange('action', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white">
                                            <option value="">All Actions</option>
                                            {ACTION_GROUPS.map(g => (
                                                <optgroup key={g.label} label={g.label}>
                                                    {g.actions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Start Date</label>
                                        <input type="date" value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">End Date</label>
                                        <input type="date" value={filters.endDate} onChange={e => handleFilterChange('endDate', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Sort By</label>
                                        <select value={`${filters.sortBy}-${filters.order}`}
                                            onChange={e => { const [s, o] = e.target.value.split('-'); handleFilterChange('sortBy', s); handleFilterChange('order', o); }}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white">
                                            <option value="createdAt-desc">Newest First</option>
                                            <option value="createdAt-asc">Oldest First</option>
                                            <option value="action-asc">Action A-Z</option>
                                            <option value="action-desc">Action Z-A</option>
                                        </select>
                                    </div>
                                </div>
                                {hasActiveFilters && (
                                    <div className="flex justify-between mt-3 pt-2 border-t border-slate-100">
                                        <span className="text-xs text-slate-400">{totalCount} results</span>
                                        <button onClick={clearFilters} className="text-xs text-slate-600 font-medium hover:text-slate-800">Clear All</button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Audit Log Table */}
                {logs.length === 0 ? (
                    <EmptyState icon={<Activity size={48} />} title="No audit logs found"
                        description={hasActiveFilters ? 'Try adjusting your filters.' : 'Audit events will appear here as they occur.'} />
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase w-44">Timestamp</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Tenant</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase hidden md:table-cell">User</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase">Action</th>
                                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase hidden lg:table-cell">Entity</th>
                                        <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase w-16">View</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {logs.map((log, idx) => {
                                        const group = getActionGroup(log.action);
                                        const GroupIcon = group?.icon || Activity;

                                        return (
                                            <motion.tr key={log.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.01 }}
                                                className="hover:bg-slate-50 transition-colors cursor-pointer group"
                                                onClick={() => navigate(`/super-admin/audit-logs/${log.id}`)}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Clock size={12} className="text-slate-400 flex-shrink-0" />
                                                        <span className="text-xs font-mono text-slate-600 whitespace-nowrap">
                                                            {formatDateTime(log.createdAt)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1.5">
                                                        <Building2 size={12} className="text-violet-400 flex-shrink-0" />
                                                        <span className="text-xs font-bold text-slate-700 truncate max-w-[120px]">
                                                            {log.tenantName}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 hidden md:table-cell">
                                                    <div className="flex items-center gap-1.5">
                                                        <User size={12} className="text-slate-400 flex-shrink-0" />
                                                        <span className="text-xs text-slate-600 truncate max-w-[100px]">
                                                            {log.userName || log.userEmail || 'System'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {group ? (
                                                        <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold', group.bg, group.color)}>
                                                            <GroupIcon size={10} />
                                                            {log.action.replace(/_/g, ' ')}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-medium text-slate-600">
                                                            {log.action.replace(/_/g, ' ')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 hidden lg:table-cell">
                                                    <span className="text-xs text-slate-500">{log.entityType?.replace(/_/g, ' ')}</span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/super-admin/audit-logs/${log.id}`); }}
                                                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <span className="text-xs text-slate-500">Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, totalCount)} of {totalCount.toLocaleString()}</span>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setPage(1)} disabled={page === 1} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronsLeft size={14} /></button>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
                                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                        let pageNum: number;
                                        if (totalPages <= 7) pageNum = i + 1;
                                        else if (page <= 4) pageNum = i + 1;
                                        else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                                        else pageNum = page - 3 + i;
                                        return (
                                            <button key={pageNum} onClick={() => setPage(pageNum)}
                                                className={clsx('w-8 h-8 rounded-lg text-xs font-medium', page === pageNum ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-200')}>{pageNum}</button>
                                        );
                                    })}
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronRight size={14} /></button>
                                    <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronsRight size={14} /></button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}